import {Feature, Point} from "geojson";
import distance from "@turf/distance";
import {featureCollection, point, polygon, Units} from "@turf/helpers";
import isobands from "@turf/isobands";
import polygonSmooth from "@turf/polygon-smooth";

import {SteppedGradient} from "../gradient";
import {BaseOverlayProps, MapContext, MapService} from "../data-map-props";
import React from "react";

const limitValue = (gradient: SteppedGradient, value: number): number => {
    return Math.max(
        gradient.breaks[0],
        Math.min(gradient.breaks[gradient.breaks.length - 1], value)
    )
}

export class ContourOverlay extends React.Component<BaseOverlayProps> {

    static contextType = MapContext;

    public context: MapService | undefined;

    static defaultProps: BaseOverlayProps = {
        overlayPosition: "under-map"
    };

    public render() {

        if (!this.context || !this.context.tree) return "";
        const context: MapService = this.context;

        const zProperty = 'value';

        // build a grid of coordinates to cover the contiguous US
        // we'll then use IDW to interpolate the data

        const [gridCellSize, gridCellUnit]: [number, Units] = [200, 'kilometers'];

        // want to minimize this bbox to reduce interpolation time
        // while still ensuring the entire continental US is covered
        const [north, west, south, east] = [
            50.5, -127, 23, -64.5
        ];

        const cellWidth = gridCellSize / distance([west, south], [east, south], {units: gridCellUnit}) * (east - west);
        const cellHeight = gridCellSize / distance([west, south], [west, north], {units: gridCellUnit}) * (north - south);

        const grid: Feature<Point, { [zProperty]: number }>[] = [];
        const powerParam = 2;

        let currentLon = west;
        while (currentLon <= east) {
            let currentLat = south;
            while (currentLat <= north) {
                // compute IDW from known points within 1000km
                let zSum = 0, wSum = 0;
                const curPos = [currentLon, currentLat];
                for (const [site, dist] of context.tree.sitesNear(currentLon, currentLat, 500)) {
                    if (!context.data.values.has(site)) {
                        continue;
                    }
                    const z = context.data.values.get(site);
                    if (dist === 0) {
                        zSum = z;
                        wSum = 1;
                        break;
                    }
                    const weight = Math.pow(dist, -powerParam);
                    zSum += z * weight;
                    wSum += weight;
                }
                grid.push(point(curPos, {[zProperty]: limitValue(context.gradient, zSum / wSum)}));
                currentLat += cellHeight;
            }
            currentLon += cellWidth;
        }

        const bands = isobands(featureCollection(grid), [
            ...context.gradient.breaks
        ], {zProperty});

        return bands.features
            // return grid
            .map((band, index1) => {
                const color = context.gradient.getColor(
                    parseFloat(band.properties[zProperty].split('-')[1])
                );
                return band.geometry.coordinates.map((poly, index2) =>
                    <path key={`${index1}-${index2}`} clipPath={context.nationClipPathRef} fill={color} d={context.path(
                        polygonSmooth(polygon(poly), {iterations: 2})
                    )} />
                );
            })
            .flat()
            .filter((p) => p !== null);

    }
}